import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import {
  AAG_SOD_DETAILS,
  AAG_SOD_ENT_DETAILS,
  violationsEntitlement,
} from '../constants';

export interface NavigateToDetailsProps {
  key: string;
  params?: Record<string, string>;
}

/**
 * A custom hook that provides navigation functionality.
 *
 * @description
 * This hook wraps the navigation logic and provides a consistent way to navigate
 * throughout the application. It can handle both string paths and object-based
 * navigation details. Additionally, you can pass optional state data when navigating,
 * which can be accessed in the target component via `useLocation`.
 *
 * @example
 * // Navigate using a string path
 * const { navigateToUrl } = useNavigateTo();
 * navigateToUrl('/dashboard');
 *
 * @example
 * // Navigate with parameters
 * const { navigateToDetails } = useNavigateTo();
 * navigateToDetails({
 *   key: '123',
 *   params: { id: '123' }
 * });
 *
 * @example
 * // Go back to previous page
 * const { navigateToBack } = useNavigateTo();
 * navigateToBack();
 *
 * @example
 * // Update only the activeTab param on current page
 * const { updateActiveTab } = useNavigateTo();
 * updateActiveTab('mitigate');
 *
 * @returns {Function} returns.updateActiveTab - Updates the activeTab param on the current URL without adding history.
 *
 * @returns {Object} An object containing navigation functions:
 * @returns {Function} returns.navigateToDetails - Navigate to details page with optional params and state.
 * @returns {Function} returns.navigateToUrl - Navigate using a direct URL string.
 * @returns {Function} returns.navigateToBack - Navigate back to the previous page.
 */
const useNavigateTo = () => {
  const history = useHistory();

  const navigateToDetails = useCallback(
    ({ key, params }: NavigateToDetailsProps): void => {
      const paramObj: Record<string, string> = {};

      if (params) {
        Object.entries(params).forEach(([paramKey, paramValue]) => {
          paramObj[paramKey] = encodeURIComponent(paramValue);
        });
      }

      const searchParams = new URLSearchParams(paramObj);
      const paramString = searchParams.toString();

      // Use AAG_SOD_ENT_DETAILS if navigating from entitlement tab context
      const isEntitlementContext = params?.type === violationsEntitlement;
      const basePath = isEntitlementContext
        ? AAG_SOD_ENT_DETAILS
        : AAG_SOD_DETAILS;

      history.push({
        pathname: `${basePath}/${key}`,
        search: `?${paramString}`,
      });
    },
    [history]
  );

  const updateActiveTab = useCallback(
    (value: string): void => {
      const currentParams = new URLSearchParams(history.location.search);

      currentParams.set('activeTab', value);

      history.replace({
        pathname: history.location.pathname,
        search: `?${currentParams.toString()}`,
      });
    },
    [history]
  );

  const navigateToUrl = useCallback(
    (url: string): void => {
      history.push(url);
    },
    [history]
  );

  const navigateToBack = useCallback((): void => {
    history.goBack();
  }, [history]);

  return { navigateToDetails, updateActiveTab, navigateToUrl, navigateToBack };
};

// eslint-disable-next-line import/prefer-default-export
export { useNavigateTo };
